/**
 * FullRequestAnalysisPane.jsx — the unified per-request "Full analysis" pane.
 *
 * Renders buildFullRequestAnalysis()'s output (utils/buildFullRequestAnalysis.js)
 * for ONE captured request/response row: request side and response side laid
 * out together, its issues, and (when a caller has them) its conformance check
 * verdicts. Presentation only — every value here is already extracted
 * somewhere on the row; this component runs no new validation.
 *
 * Styling matches the plain-inline-style idiom already used inside
 * InspectraNetworkView.jsx's detail pane (RequestDetails / AdInspectView /
 * AdDebugModal's GAMInfoPanel): `var(--*)` theme tokens, KV rows, uppercase
 * section headers, `<details>` for large collapsible blocks, and small copy
 * buttons matching AdInspectView's "Copy cust_params" button.
 *
 * `checkVerdicts` (GAM/APS param conformance attached to this row) is `[]`
 * from the builder today — no per-row-filtered conformance data exists yet,
 * only session-wide results in AgentPanel.jsx. When rows ARE supplied (via
 * `apsParamRows` / `gamParamRows` props, pre-filtered to this row by a future
 * caller), this component reuses the real table components rather than
 * re-rendering their data by hand, per the existing convention.
 */
import React from 'react';
import ApsParamConformanceTable from './ApsParamConformanceTable';
import ParamConformanceTable from './ParamConformanceTable';
import { buildFullRequestAnalysis } from '../utils/buildFullRequestAnalysis';

const SEV_COLOR = {
  error: { bg: 'var(--error-bg, #fdecea)', border: 'var(--error-border, #f5c2c0)', text: 'var(--error-text, #b71c1c)' },
  warning: { bg: 'var(--warn-bg, #fffbeb)', border: 'var(--warn-border, var(--line))', text: 'var(--warn-text, #92400e)' },
  pass: { bg: 'var(--success-bg, #edfaf3)', border: 'var(--success-border, #b7e4cc)', text: 'var(--success-text, #1a6f3c)' },
  info: { bg: 'var(--info-bg, #eef6fc)', border: 'var(--info-border, #bcdcf2)', text: 'var(--info-text, #1c5f8a)' },
};

function copyToClipboard(text) {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(String(text));
      return true;
    }
  } catch { /* fall through to legacy path */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = String(text);
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    document.execCommand('copy');
    ta.remove();
    return true;
  } catch { return false; }
}

function Section({ title, right, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        fontSize: 10, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase',
        letterSpacing: 1, marginBottom: 4, paddingBottom: 3, borderBottom: '2px solid var(--line)',
      }}>
        <span>{title}</span>
        {right}
      </div>
      {children}
    </div>
  );
}

function CopyBtn({ text, label = 'Copy' }) {
  if (!text) return null;
  return (
    <button
      onClick={() => copyToClipboard(text)}
      style={{ fontSize: 10, padding: '2px 8px', border: '1px solid var(--line)', borderRadius: 4, background: 'transparent', color: 'var(--muted)', cursor: 'pointer' }}
      title="Copy to clipboard"
    >{label}</button>
  );
}

function KV({ label, val, mono }) {
  if (val == null || val === '') return null;
  return (
    <div style={{ display: 'flex', gap: 8, padding: '3px 0', borderBottom: '1px solid var(--line)', alignItems: 'flex-start', minWidth: 0 }}>
      <span style={{ fontSize: 11, color: 'var(--muted)', width: 150, flexShrink: 0, paddingTop: 1 }}>{label}</span>
      <span style={{ fontSize: 11, color: 'var(--ink)', fontFamily: mono ? 'var(--font-mono)' : undefined, wordBreak: 'break-all', flex: 1 }}>{val}</span>
    </div>
  );
}

function ParamList({ obj, mono = true }) {
  const entries = Object.entries(obj || {});
  if (!entries.length) return <span style={{ fontSize: 11, color: 'var(--muted)' }}>none</span>;
  return (
    <div>
      {entries.map(([k, v]) => (
        <KV key={k} label={k} val={String(v).length > 200 ? `${String(v).slice(0, 200)}…` : String(v)} mono={mono} />
      ))}
    </div>
  );
}

function IssueRow({ issue }) {
  const tone = SEV_COLOR[issue.severity] || SEV_COLOR.info;
  return (
    <div style={{
      display: 'flex', gap: 8, alignItems: 'flex-start', padding: '6px 8px', marginBottom: 4,
      borderRadius: 6, background: tone.bg, border: `1px solid ${tone.border}`,
    }}>
      <span style={{ fontSize: 10, fontWeight: 700, color: tone.text, textTransform: 'uppercase', flexShrink: 0, minWidth: 46 }}>
        {issue.severity}
      </span>
      <span style={{ fontSize: 11, color: tone.text, flex: 1, minWidth: 0, wordBreak: 'break-word' }}>
        {issue.code ? <span style={{ fontFamily: 'var(--font-mono)', marginRight: 6 }}>[{issue.code}]</span> : null}
        {issue.message || '(no message)'}
      </span>
    </div>
  );
}

function BidsTable({ bids }) {
  if (!bids || !bids.length) return <span style={{ fontSize: 11, color: 'var(--muted)' }}>No Prebid bids on this row.</span>;
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
        <thead>
          <tr>
            {['seat', 'status', 'price', 'latencyMs'].map((h) => (
              <th key={h} style={{ textAlign: 'left', padding: '3px 6px', color: 'var(--muted)', borderBottom: '1px solid var(--line)', fontWeight: 600 }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {bids.map((b, i) => (
            <tr key={i}>
              <td style={{ padding: '3px 6px', borderBottom: '1px solid var(--line)' }}>{b?.seat ?? '—'}</td>
              <td style={{ padding: '3px 6px', borderBottom: '1px solid var(--line)' }}>{b?.status ?? '—'}</td>
              <td style={{ padding: '3px 6px', borderBottom: '1px solid var(--line)' }}>{b?.price ?? '—'}</td>
              <td style={{ padding: '3px 6px', borderBottom: '1px solid var(--line)' }}>{b?.latencyMs ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * @param {object} props
 * @param {object} props.row - the raw request/response row (as displayed in the requests table).
 * @param {Array}  [props.apsParamRows] - optional, pre-filtered to this row: rows shaped like
 *   buildApsParamConformance.js's output, rendered via ApsParamConformanceTable when present.
 * @param {Array}  [props.gamParamRows] - optional, pre-filtered to this row: rows shaped like
 *   buildParamConformance.js's output, rendered via ParamConformanceTable when present.
 * @param {string} [props.sessionPlatform] - forwarded to ApsParamConformanceTable.
 */
export default function FullRequestAnalysisPane({ row, apsParamRows, gamParamRows, sessionPlatform }) {
  const analysis = buildFullRequestAnalysis(row);
  const { request, response, issues, timestamp, meta } = analysis;

  const hasAps = Array.isArray(apsParamRows) && apsParamRows.length > 0;
  const hasGam = Array.isArray(gamParamRows) && gamParamRows.length > 0;

  const fullUrl = request.url || '';

  return (
    <div style={{ padding: '12px 14px', overflowY: 'auto', fontSize: 12 }}>
      {!analysis.ok && (
        <div style={{ marginBottom: 12, padding: '8px 12px', borderRadius: 6, fontSize: 11, background: 'var(--warn-bg, #fffbeb)', border: '1px solid var(--warn-border, var(--line))', color: 'var(--warn-text, #92400e)' }}>
          This row could not be fully read — showing what's available.
        </div>
      )}

      {/* Header strip: method/host/path + timestamp */}
      <Section title="Request">
        <KV label="Method" val={request.method} mono />
        <KV label="Host" val={request.host} mono />
        <KV label="Path" val={request.path} mono />
        <KV label="Request type" val={meta.requestType} />
        <KV label="Timestamp" val={timestamp.iso} mono />
        <KV label="Device" val={typeof meta.device === 'string' ? meta.device : (meta.device ? JSON.stringify(meta.device) : null)} />
        <KV label="Step" val={meta.step} />
        <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
          <CopyBtn text={fullUrl} label="Copy URL" />
        </div>
      </Section>

      {/* Request query params */}
      <Section
        title={`Query params (${Object.keys(request.queryParams).length})`}
        right={<CopyBtn text={Object.entries(request.queryParams).map(([k, v]) => `${k}=${v}`).join('\n')} label="Copy all" />}
      >
        <ParamList obj={request.groupedQueryParams.other} />
        {Object.keys(request.groupedQueryParams.bid).length > 0 && (
          <details open style={{ marginTop: 6 }}>
            <summary style={{ cursor: 'pointer', fontSize: 10, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 1, padding: '3px 0', userSelect: 'none' }}>
              Bid params (hb_ / amzn) ({Object.keys(request.groupedQueryParams.bid).length})
            </summary>
            <ParamList obj={request.groupedQueryParams.bid} />
          </details>
        )}
        {Object.keys(request.groupedQueryParams.contextual).length > 0 && (
          <details style={{ marginTop: 6 }}>
            <summary style={{ cursor: 'pointer', fontSize: 10, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 1, padding: '3px 0', userSelect: 'none' }}>
              Contextual signals (mobian_) ({Object.keys(request.groupedQueryParams.contextual).length})
            </summary>
            <ParamList obj={request.groupedQueryParams.contextual} />
          </details>
        )}
      </Section>

      {/* GAM cust_params — parsed from the raw URL */}
      {Object.keys(request.custParams).length > 0 && (
        <Section
          title={`cust_params (${Object.keys(request.custParams).length})`}
          right={<CopyBtn text={Object.entries(request.custParams).map(([k, v]) => `${k}=${v}`).join('\n')} label="Copy" />}
        >
          <ParamList obj={request.custParams} />
        </Section>
      )}

      {/* Request headers */}
      {request.headers && Object.keys(request.headers).length > 0 && (
        <details style={{ marginBottom: 14 }}>
          <summary style={{ cursor: 'pointer', fontSize: 10, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 1, padding: '3px 0', borderBottom: '2px solid var(--line)', userSelect: 'none' }}>
            Request headers ({Object.keys(request.headers).length})
          </summary>
          <ParamList obj={request.headers} />
        </details>
      )}

      {/* Response */}
      <Section title="Response">
        <KV label="Status" val={response.status != null ? `${response.status}${response.statusText ? ` ${response.statusText}` : ''}` : null} />
        <KV label="MIME" val={response.mime} mono />
        {!response.bodyAvailable && (
          <div style={{ marginTop: 6, padding: '6px 10px', borderRadius: 6, fontSize: 11, background: 'var(--warn-bg, #fffbeb)', border: '1px solid var(--warn-border, var(--line))', color: 'var(--warn-text, #92400e)' }}>
            {response.bodyNote}
          </div>
        )}
      </Section>

      {/* Prebid bids — validData.tables.bids, not validData.bids */}
      <Section title={`Prebid bids (${response.prebidBids.length})`}>
        <BidsTable bids={response.prebidBids} />
      </Section>

      {/* VAST summary, when this is a video row */}
      {response.vastSummary && (
        <Section title="VAST summary">
          <KV label="Filled" val={response.vastSummary.filled === true ? 'yes' : response.vastSummary.filled === false ? 'no' : null} />
          {response.vastSummary.creative && (
            <KV label="Creative" val={typeof response.vastSummary.creative === 'string' ? response.vastSummary.creative : JSON.stringify(response.vastSummary.creative)} mono />
          )}
          {response.vastSummary.vmap && (
            <KV label="Pod (VMAP)" val={typeof response.vastSummary.vmap === 'string' ? response.vastSummary.vmap : JSON.stringify(response.vastSummary.vmap)} mono />
          )}
        </Section>
      )}

      {/* SDK summary passthrough (ATT, EIDs, device, …) when present */}
      {response.sdkSummary && Object.keys(response.sdkSummary).length > 0 && (
        <details style={{ marginBottom: 14 }}>
          <summary style={{ cursor: 'pointer', fontSize: 10, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 1, padding: '3px 0', borderBottom: '2px solid var(--line)', userSelect: 'none' }}>
            SDK summary
          </summary>
          <ParamList obj={response.sdkSummary} mono={false} />
        </details>
      )}

      {/* Issues */}
      <Section title={`Issues (${issues.length})`}>
        {issues.length === 0
          ? <span style={{ fontSize: 11, color: 'var(--muted)' }}>No issues recorded for this request.</span>
          : issues.map((iss, i) => <IssueRow key={i} issue={iss} />)}
      </Section>

      {/* Check verdicts — reuse the existing table components, never re-render by hand */}
      <Section title="Check verdicts">
        {hasAps && <ApsParamConformanceTable rows={apsParamRows} sessionPlatform={sessionPlatform} />}
        {hasGam && <ParamConformanceTable rows={gamParamRows} />}
        {!hasAps && !hasGam && (
          <span style={{ fontSize: 11, color: 'var(--muted)' }}>
            No conformance check results are attached to this request. GAM/APS param
            conformance is currently computed session-wide (Checks → AgentPanel), not
            per request — run Checks for a per-param view of the whole session.
          </span>
        )}
      </Section>
    </div>
  );
}
